import {
    PauseCircleOutlined,
    PlayCircleOutlined,
    PlusOutlined
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { ConfigProvider, Dropdown, Flex, Tabs } from "antd";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import HSelect from "../../components/custom/input/select/Select";
import HTable from "../../components/custom/table/HTable";
import { AppContext } from "../../context/AppContext";
import useHttp from "../../hooks/useHttp";
import { successNotification } from "../utils/CommonNotifications";
import { DeleteComponent, EmptyListView } from "../utils/CommonVessels";
import Input from "../../components/custom/input/input/Input";


export type WorkflowTriggerType = "Trigger.Message.Inbound" | "Trigger.Message.Outbound" | "INBOUND" | "OUTBOUND";

export interface WorkflowApiRecord {
    osyncId?: string;
    workflowId: string;
    name: string;
    status: string;
    triggerType: WorkflowTriggerType;
    revision?: number;
    entryNodeId?: string;
    applyOncePerContact?: boolean;
    defaultFlow?: boolean;
    createdAt?: string;
    updatedAt?: string;
    totalRunsCount?: number | null;
    count?: number | null;
}

export interface WorkflowRecord {
    workflowId: string;
    workflowName: string;
    status: string;
    triggerType: string;
    direction: "Incoming" | "Outgoing";
    runs: number;
    defaultFlow?: boolean;
    createdTime?: string;
    updatedTime?: string;
}

function mapApiToWorkflowRecord(api: WorkflowApiRecord): WorkflowRecord {
    const isInbound = api.triggerType?.toUpperCase().includes("INBOUND") ?? false;
    const direction: WorkflowRecord["direction"] = isInbound ? "Incoming" : "Outgoing";
    const runs =
        typeof api.totalRunsCount === "number" ? api.totalRunsCount : typeof api.count === "number" ? api.count : 0;
    return {
        workflowId: api.workflowId,
        workflowName: api.name,
        status: api.status,
        triggerType: api.triggerType,
        direction,
        runs,
        defaultFlow: api.defaultFlow ?? false,
        createdTime: api.createdAt,
        updatedTime: api.updatedAt,
    };
}

export interface TriggerRulesListProps {
    onWorkflowClick: (workflow: WorkflowRecord) => void;
    onNewWorkflow: () => void;
    /** When set, auto-select workflow matching this id once data is loaded */
    initialWorkflowId?: string | null;
}

type FlowTabKey = "custom" | "predefined";

const TriggerRulesList: React.FC<TriggerRulesListProps> = ({ onWorkflowClick, onNewWorkflow, initialWorkflowId }) => {
    const [appProperties] = useContext(AppContext);
    const { fetchDataByPagination, fetchData } = useHttp();
    const [activeTab, setActiveTab] = useState<FlowTabKey>("custom");
    const tableRef = useRef(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const paginationRef = useRef<{
        handleNext: () => void;
        handlePrev: () => void;
        hasNext: boolean;
        pageNumber: number;
        reloadTable: () => void;
    } | null>(null);
    const [searchInputValue, setSearchInputValue] = useState("");
    const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");
    const [triggerTypeFilter, setTriggerTypeFilter] = useState<
        "Trigger.Message.Inbound" | "Trigger.Message.Outbound" | null
    >(null);
    const [currentPageData, setCurrentPageData] = useState<WorkflowRecord[]>([]);
    const PAGE_SIZE = 10;

    const requestWorkflows = useCallback(
        async (params: {
            cursor?: { cursor?: string; lastKey?: string; lastRange?: string };
            limit?: number;
            signal?: AbortSignal;
            searchKeyword?: string;
            flowType?: FlowTabKey;
            triggerType?: "Trigger.Message.Inbound" | "Trigger.Message.Outbound" | null;
        }) => {
            const { limit, cursor, signal, searchKeyword, flowType, triggerType } = params;
            try {
                const searchParams = new URLSearchParams();
                if (searchKeyword?.trim()) {
                    searchParams.set("searchText", searchKeyword.trim());
                }
                if (flowType) {
                    searchParams.set("flowType", flowType);
                }
                if (triggerType) {
                    searchParams.set("triggerType", triggerType);
                }
                const query = searchParams.toString();
                const url = query ? `workflows?${query}` : "workflows";

                const res = await fetchDataByPagination(
                    url,
                    "GET",
                    null,
                    appProperties,
                    null,
                    limit ?? PAGE_SIZE,
                    cursor ?? null,
                    null,
                    signal
                );
                if (signal?.aborted) return { data: [], success: false, nextCursor: null };

                const rawItems = Array.isArray(res) ? res : (res?.data ?? res?.workflows ?? res?.items ?? []);
                const pagination = res?.paginationModel ?? res?.pagination;
                const nextCursor = pagination?.cursor
                    ? { cursor: pagination.cursor }
                    : pagination?.lastKey
                      ? { lastKey: pagination.lastKey, lastRange: pagination.lastRange ?? null }
                      : null;

                const mapped = rawItems.map((item: WorkflowApiRecord) => mapApiToWorkflowRecord(item));
                const isPredefinedTab = flowType === "predefined";
                const filtered = isPredefinedTab
                    ? mapped.filter((r: WorkflowApiRecord) => r.defaultFlow === true)
                    : mapped.filter((r: WorkflowApiRecord) => r.defaultFlow !== true);
                setCurrentPageData(filtered);

                return { data: filtered, success: true, nextCursor };
            } catch {
                setCurrentPageData([]);
                return { data: [], success: false, nextCursor: null };
            }
        },
        [appProperties, fetchDataByPagination]
    );

    const hasRestoredFromUrl = useRef(false);
    useEffect(() => {
        if (
            initialWorkflowId &&
            initialWorkflowId !== "new" &&
            currentPageData.length > 0 &&
            !hasRestoredFromUrl.current
        ) {
            const match = currentPageData.find((w) => w.workflowId === initialWorkflowId);
            if (match) {
                hasRestoredFromUrl.current = true;
                onWorkflowClick(match);
            }
        }
    }, [initialWorkflowId, currentPageData, onWorkflowClick]);

    const triggerReload = useCallback(() => {
        paginationRef.current?.reloadTable?.();
    }, []);

    const handleTabChange = useCallback((key: string) => {
        setActiveTab(key as FlowTabKey);
        requestAnimationFrame(() => paginationRef.current?.reloadTable?.());
    }, []);

    const handleFilterSelect = useCallback((value: "Trigger.Message.Inbound" | "Trigger.Message.Outbound" | null) => {
        setTriggerTypeFilter(value);
        requestAnimationFrame(() => paginationRef.current?.reloadTable?.());
    }, []);

    const handleSearchApply = useCallback(() => {
        setAppliedSearchKeyword(searchInputValue.trim());
        requestAnimationFrame(() => paginationRef.current?.reloadTable?.());
    }, [searchInputValue]);

    const handleSearchClear = useCallback(() => {
        setSearchInputValue("");
        setAppliedSearchKeyword("");
        requestAnimationFrame(() => paginationRef.current?.reloadTable?.());
    }, []);

    const [deleteModalRecord, setDeleteModalRecord] = useState<WorkflowRecord | null>(null);

    const handleDisable = useCallback(
        async (record: WorkflowRecord, e: React.MouseEvent) => {
            e?.stopPropagation?.();
            if (!appProperties || !record.workflowId) return;
            try {
                await fetchData(`workflows/${record.workflowId}/disable`, "POST", null, appProperties);
                successNotification("Workflow disabled successfully.");
                triggerReload();
            } catch {
                // Error handled by useHttp
            }
        },
        [appProperties, fetchData, triggerReload]
    );

    const handleEnable = useCallback(
        async (record: WorkflowRecord, e: React.MouseEvent) => {
            e?.stopPropagation?.();
            if (!appProperties || !record.workflowId) return;
            try {
                await fetchData(`workflows/${record.workflowId}/enable`, "POST", null, appProperties);
                successNotification("Workflow enabled successfully.");
                triggerReload();
            } catch {
                // Error handled by useHttp
            }
        },
        [appProperties, fetchData, triggerReload]
    );

    const handleDeleteClick = useCallback((record: WorkflowRecord, e: React.MouseEvent) => {
        e?.stopPropagation?.();
        setDeleteModalRecord(record);
    }, []);

    const handleDeleteConfirm = useCallback(
        async (record: WorkflowRecord) => {
            if (!appProperties || !record.workflowId) return;
            try {
                await fetchData(`workflows/${record.workflowId}`, "DELETE", null, appProperties);
                successNotification("Workflow deleted successfully.");
                setDeleteModalRecord(null);
                triggerReload();
            } catch {
                // Error handled by useHttp
            }
        },
        [appProperties, fetchData, triggerReload]
    );

    const getActionsMenuItems = useCallback(
        (record: WorkflowRecord): MenuProps["items"] => {
            const isPredefined = activeTab === "predefined";
            if (isPredefined) {
                return [];
            }
    
            const isPaused = (record.status ?? "").toLowerCase() === "paused" || (record.status ?? "").toLowerCase() === "draft";
            const items: MenuProps["items"] = [];
    
            const stopAnd =
                (fn: (e: React.MouseEvent) => void) =>
                (info: { domEvent: React.MouseEvent | React.KeyboardEvent }) => {
                    info.domEvent.stopPropagation();
                    fn(info.domEvent as React.MouseEvent);
                };
    
            if (isPaused) {
                items.push({
                    key: "enable",
                    label: "Enable",
                    icon: <PlayCircleOutlined className="pe-3" />,
                    onClick: stopAnd((e) => handleEnable(record, e)),
                });
            } else {
                items.push({
                    key: "disable",
                    label: "Disable",
                    icon: <PauseCircleOutlined className="pe-3" />,
                    onClick: stopAnd((e) => handleDisable(record, e)),
                });
            }
    
            items.push({
                key: "delete",
                label: "Delete",
                icon: <div className="actionIconsSprite deleteTemplate-icon" />,
                danger: true,
                onClick: stopAnd((e) => handleDeleteClick(record, e)),
            });
    
            return items;
        },
        [activeTab, handleDisable, handleEnable, handleDeleteClick],
    );

    const columns = [
        {
            title: "Workflow Name",
            dataIndex: "workflowName",
            key: "workflowName",
            width: "24%",
            render: (text: string) => <span className="trigger-rules-list__name">{text}</span>,
        },
        {
            title: "Trigger Type",
            dataIndex: "triggerType",
            key: "triggerType",
            width: "16%",
            render: (text: string): React.ReactNode => {
                const parts = (text ?? "").split(".");
                const middle = parts.length >= 2 ? parts[1] : text;

                return <span className="trigger-rules-list__cell-text">{middle}</span>;
            },
        },
        {
            title: "Direction",
            dataIndex: "direction",
            key: "direction",
            width: "16%",
            render: (direction: WorkflowRecord["direction"]) => (
                <span
                    className={`trigger-rules-list__direction rounded-5 trigger-rules-list__direction--${direction.toLowerCase()}`}
                >
                    {direction === "Incoming" ? (
                        <div className="automationIconsSprite incoming-drirection-icon"></div>
                    ) : (
                        <div className="automationIconsSprite outgoing-direction-icon"></div>
                    )}
                    {direction}
                </span>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: "14%",
            render: (status: string) => (
                <span
                    className={`trigger-rules-list__status trigger-rules-list__status--${(status ?? "").toLowerCase()}`}
                >
                    <span className="trigger-rules-list__status-dot" />
                    {status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : status}
                </span>
            ),
        },
        {
            title: "Stats",
            dataIndex: "runs",
            key: "runs",
            width: "20%",
            render: (runs?: number) => (
                <span className="trigger-rules-list__runs">
                    <span className="trigger-rules-list__runs-count">{(runs ?? 0).toLocaleString()}</span>
                    <span className="trigger-rules-list__runs-label">runs</span>
                </span>
            ),
        },
        ...(activeTab !== "predefined"
            ? [
                  {
                      title: "",
                      dataIndex: "workflowId",
                      key: "actions",
                      width: "10%",
                      align: "right" as const,
                      render: (_: unknown, record: WorkflowRecord): React.ReactNode => {
                          const menuItems = getActionsMenuItems(record);
                          if (!menuItems || menuItems.length === 0) {
                              return null;
                          }
                          return (
                              <Dropdown
                                  menu={{
                                      items: menuItems,
                                      onClick: (info) => info.domEvent.stopPropagation(),
                                  }}
                                  trigger={["click"]}
                                  placement="bottomRight"
                                  rootClassName="editDeleteSettingDropdown"
                              >
                                  <div
                                      style={{ cursor: "pointer" }}
                                      className="actionIconsSprite editDeleteSettingsIcon"
                                      onClick={(e) => e.stopPropagation()}
                                  ></div>
                              </Dropdown>
                          );
                      },
                  },
              ]
            : []),
    ];

    return (
        <Flex ref={tableContainerRef} className="trigger-rules-list flex-column h-100 overflow-hidden m-3 rounded-2">
            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            colorPrimary: "var(--hs-violet)",
                            inkBarColor: "var(--hs-violet)",
                            itemActiveColor: "#000000",
                            itemHoverColor: "var(--hs-violet)",
                        },
                    },
                }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    className="trigger-rules-list__tabs"
                    items={[
                        { key: "custom", label: "Custom" },
                        { key: "predefined", label: "Pre-Defined Flows" },
                    ]}
                />
            </ConfigProvider>
            {/* @ts-expect-error HTable uses ui/data/toolbar - ProTable types expect flat props */}
            <HTable
                tableRef={tableRef}
                tableContainerRef={tableContainerRef}
                heightToBeSubtractedFromContainerHeight={200}
                ui={{
                    columns,
                    rowKey: "workflowId",
                    title: (
                        <Flex align="center" gap={20}>
                            <HSelect
                                options={[{ label: "All", value: "" }, { label: "Inbound", value: "Trigger.Message.Inbound" }, { label: "Outbound", value: "Trigger.Message.Outbound" }]}
                                value={searchInputValue}
                                onChange={(value: string) => {
                                    setSearchInputValue(value);
                                    handleSearchApply();
                                }}
                                placeholder="Trigger type"
                                customProps={{ className: "mt-2", style: { width: 150 } }}
                            />
                            <Input placeholder="Search by name" allowClear style={{ width: 200 }} />

                        </Flex>
                    ),
                }}
                data={{
                    request: requestWorkflows,
                    requestExtraParams: {
                        searchKeyword: appliedSearchKeyword,
                        flowType: activeTab,
                        triggerType: triggerTypeFilter,
                    },
                }}
                toolbar={{
                    buttonText: "New Trigger Rule",
                    onButtonClick: activeTab === "custom" ? onNewWorkflow : undefined,
                    buttonIcon: <PlusOutlined />,
                }}
                proProps={{
                    locale: {
                        emptyText: (
                            <EmptyListView
                                icon=""
                                tagLineOne="No workflows yet"
                                tagLineTwo="Create your first trigger rule automation"
                                tagHeight="32vh"
                            />
                        ),
                    },
                    onRow: (record: WorkflowRecord) => ({
                        onClick: () => onWorkflowClick(record),
                        style: { cursor: "pointer" },
                    }),
                }}
            />

            {deleteModalRecord && (
                <DeleteComponent
                    modalOpen={!!deleteModalRecord}
                    setModalOpen={(open: boolean) => !open && setDeleteModalRecord(null)}
                    record={deleteModalRecord}
                    confirmDeleteIcon="hs-delete-icon"
                    confirmDeleteBtnName="Delete"
                    confirmDeleteComponentTitle="Delete workflow?"
                    confirmDeleteComponent="Are you sure you want to delete this workflow? This action cannot be undone."
                    appProperties={appProperties}
                    handleDelete={() => handleDeleteConfirm(deleteModalRecord)}
                />
            )}
        </Flex>
    );
};

export default TriggerRulesList;
